import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { formatDate } from '@/utils/dateFormat'
import { useUser } from '@/hooks/useUser'
import { PageHeader } from '@/components/ui/PageHeader'
import { Modal } from '@/components/ui/Modal'
import { getUserRoles, UserRolesResponse } from '@/api/users'
import { ChevronLeft, AlertCircle, Shield, Users as UsersIcon } from 'lucide-react'

// Human-readable labels for the platform role strings stored on
// LabAccessGroup.roles. Keep in sync with backend Role enum (auth/roles.py).
const ROLE_LABELS: Record<string, string> = {
  platform_admin: 'Platform Admin',
  manage_tap: 'TAP – HR Officer',
  tap_approver: 'TAP – Approver',
}

const ROLE_DESCRIPTIONS: Record<string, string> = {
  platform_admin: 'Full administrative access across the platform.',
  manage_tap: 'Can create and manage applications in the Talent Acquisition Platform.',
  tap_approver: 'One-up approver for TAP assessments and offers.',
}

interface UserData {
  id: string
  displayName: string
  email: string
  jobTitle?: string
  department?: string
  status: 'Active' | 'Inactive'
  lastLogin?: string
  lastSynced?: string
  entraProfileUrl?: string
}

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user, isLoading, error } = useUser(id || '')
  const [activeTab, setActiveTab] = useState<'profile' | 'memberships' | 'audit'>('profile')
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editData, setEditData] = useState<Partial<UserData>>({})

  // Feature #144: lazy-load the roles/memberships payload the first time the
  // user opens the tab. Avoids wasting a roundtrip if they never click into
  // it, and keeps the initial user-detail page load minimal.
  const [rolesData, setRolesData] = useState<UserRolesResponse | null>(null)
  const [rolesLoading, setRolesLoading] = useState(false)
  const [rolesError, setRolesError] = useState<string | null>(null)

  useEffect(() => {
    if (activeTab !== 'memberships' || !id || rolesData || rolesLoading) return
    setRolesLoading(true)
    setRolesError(null)
    getUserRoles(id)
      .then((data) => setRolesData(data))
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'Failed to load roles'
        setRolesError(message)
      })
      .finally(() => setRolesLoading(false))
  }, [activeTab, id, rolesData, rolesLoading])

  if (isLoading) {
    return (
      <div style={{ padding: '24px' }}>
        <button
          onClick={() => navigate('/users')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--color-magenta)',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '20px',
            padding: 0,
            fontFamily: 'var(--font-family)',
          }}
        >
          <ChevronLeft size={18} />
          Back to users
        </button>
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
          Loading user...
        </div>
      </div>
    )
  }

  if (error || !user) {
    return (
      <div style={{ padding: '24px' }}>
        <button
          onClick={() => navigate('/users')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--color-magenta)',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '20px',
            padding: 0,
            fontFamily: 'var(--font-family)',
          }}
        >
          <ChevronLeft size={18} />
          Back to users
        </button>
        <div
          style={{
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            padding: '16px',
            display: 'flex',
            gap: '10px',
          }}
        >
          <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0 }} />
          <div>
            <p style={{ margin: 0, color: '#dc2626', fontWeight: 500 }}>User not found</p>
            <p style={{ margin: '4px 0 0 0', color: '#991b1b', fontSize: '13px' }}>{error?.message ?? 'Unable to load user details'}</p>
          </div>
        </div>
      </div>
    )
  }

  const getInitials = (name?: string | null) => {
    if (!name || !name.trim()) return '?'
    return name
      .trim()
      .split(/\s+/)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
  }

  return (
    <div style={{ padding: '0' }}>
      <div style={{ padding: '24px', paddingBottom: '16px' }}>
        <button
          onClick={() => navigate('/users')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: 'var(--color-magenta)',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '20px',
            padding: 0,
            fontFamily: 'var(--font-family)',
          }}
        >
          <ChevronLeft size={18} />
          Back to users
        </button>

        {/* Header with user info and edit button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 700, margin: '0 0 8px 0', color: 'var(--color-black)' }}>
              {user.displayName}
            </h1>
            <p style={{ margin: 0, color: 'var(--color-dark-grey)', fontSize: '14px' }}>{user.email}</p>
          </div>
          <button
            onClick={() => {
              setEditData(user)
              setEditModalOpen(true)
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--color-magenta)',
              color: 'var(--color-white)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-family)',
            }}
          >
            Edit
          </button>
        </div>
      </div>

      {/* Tab navigation */}
      <div style={{ borderBottom: '1px solid var(--color-light-grey)', padding: '0 24px' }}>
        <div style={{ display: 'flex', gap: '24px' }}>
          {(['profile', 'memberships', 'audit'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 0',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                color: activeTab === tab ? 'var(--color-magenta)' : 'var(--color-dark-grey)',
                borderBottom: activeTab === tab ? '2px solid var(--color-magenta)' : 'none',
                fontFamily: 'var(--font-family)',
                textTransform: 'capitalize',
              }}
            >
              {tab === 'memberships' ? 'OU memberships' : tab === 'audit' ? 'Audit history' : 'Profile'}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div style={{ padding: '24px' }}>
        {activeTab === 'profile' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
            {/* Avatar */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-magenta)',
                  color: 'var(--color-white)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  fontWeight: 700,
                  marginBottom: '16px',
                }}
              >
                {getInitials(user.displayName)}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--color-dark-grey)' }}>
                Status: <strong style={{ color: user.isActive ? '#0284c7' : '#6b7280' }}>{user.isActive ? 'Active' : 'Inactive'}</strong>
              </div>
            </div>

            {/* User fields */}
            <div style={{ display: 'grid', gap: '12px' }}>
              {[
                { label: 'Display name', value: user.displayName },
                { label: 'Email', value: user.email },
                { label: 'Job title', value: user.jobTitle || '-' },
                { label: 'Department', value: user.department || '-' },
                { label: 'Last login', value: user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never' },
                { label: 'Last synced', value: user.lastSyncedAt ? formatDate(user.lastSyncedAt) : '-' },
              ].map((field) => (
                <div key={field.label} style={{ padding: '12px', backgroundColor: 'var(--color-white)', borderRadius: '6px' }}>
                  <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginBottom: '4px', fontWeight: 500 }}>
                    {field.label}
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--color-black)' }}>{field.value}</div>
                </div>
              ))}

              {user.entraProfileUrl && (
                <a
                  href={user.entraProfileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '12px',
                    backgroundColor: 'var(--color-white)',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    color: 'var(--color-magenta)',
                    fontWeight: 500,
                    fontSize: '14px',
                    border: '1px solid var(--color-light-grey)',
                  }}
                >
                  View in Entra ID
                </a>
              )}
            </div>
          </div>
        )}

        {activeTab === 'memberships' && (
          <div style={{ color: 'var(--color-dark-grey)' }}>
            {rolesLoading && !rolesData && (
              <p style={{ textAlign: 'center', padding: '20px 0' }}>Loading roles and memberships...</p>
            )}

            {rolesError && (
              <div
                style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'flex-start',
                }}
              >
                <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p style={{ margin: 0, color: '#dc2626', fontWeight: 500 }}>Unable to load roles</p>
                  <p style={{ margin: '4px 0 0 0', color: '#991b1b', fontSize: '13px' }}>{rolesError}</p>
                </div>
              </div>
            )}

            {rolesData && (
              <div style={{ display: 'grid', gap: '28px' }}>
                {/* Effective roles */}
                <section>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <Shield size={18} style={{ color: 'var(--color-magenta)' }} />
                    <h2 style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: 'var(--color-black)' }}>
                      Effective roles
                    </h2>
                  </div>
                  <p style={{ margin: '0 0 12px 0', fontSize: '13px' }}>
                    Roles {user.displayName} holds, derived from their Entra group memberships that are configured as access groups.
                  </p>
                  {rolesData.roles.length === 0 ? (
                    <p
                      style={{
                        padding: '12px 16px',
                        backgroundColor: 'var(--color-light-grey)',
                        borderRadius: '6px',
                        margin: 0,
                        fontSize: '13px',
                      }}
                    >
                      No roles — this user has no membership in an active access group.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {rolesData.roles.map((role) => (
                        <span
                          key={role}
                          title={ROLE_DESCRIPTIONS[role] || role}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '14px',
                            backgroundColor: 'var(--color-magenta)',
                            color: 'var(--color-white)',
                            fontSize: '13px',
                            fontWeight: 500,
                          }}
                        >
                          {ROLE_LABELS[role] || role}
                        </span>
                      ))}
                    </div>
                  )}
                </section>

                {/* Access groups (mapped OUs) */}
                <section>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <UsersIcon size={18} style={{ color: 'var(--color-magenta)' }} />
                    <h2 style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: 'var(--color-black)' }}>
                      Access groups
                    </h2>
                  </div>
                  <p style={{ margin: '0 0 12px 0', fontSize: '13px' }}>
                    Entra groups the user is a member of that are configured as access groups in The Lab.
                  </p>
                  {rolesData.accessGroups.length === 0 ? (
                    <p
                      style={{
                        padding: '12px 16px',
                        backgroundColor: 'var(--color-light-grey)',
                        borderRadius: '6px',
                        margin: 0,
                        fontSize: '13px',
                      }}
                    >
                      None of this user's Entra group memberships match a configured access group.
                    </p>
                  ) : (
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {rolesData.accessGroups.map((g) => (
                        <div
                          key={g.id}
                          style={{
                            border: '1px solid var(--color-light-grey)',
                            borderRadius: '6px',
                            padding: '12px 16px',
                            backgroundColor: 'var(--color-white)',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--color-black)' }}>
                                {g.displayName}
                                {!g.isActive && (
                                  <span
                                    style={{
                                      marginLeft: '8px',
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                      backgroundColor: '#f3f4f6',
                                      color: '#6b7280',
                                      fontSize: '11px',
                                      fontWeight: 500,
                                    }}
                                  >
                                    Inactive
                                  </span>
                                )}
                              </div>
                              {g.description && (
                                <p style={{ margin: '4px 0 0 0', fontSize: '13px' }}>{g.description}</p>
                              )}
                              <p style={{ margin: '4px 0 0 0', fontSize: '11px', fontFamily: 'monospace' }}>
                                {g.entraObjectId}
                              </p>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'flex-end' }}>
                              {g.roles.length === 0 ? (
                                <span style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>No roles</span>
                              ) : (
                                g.roles.map((role) => (
                                  <span
                                    key={role}
                                    style={{
                                      padding: '3px 8px',
                                      borderRadius: '10px',
                                      backgroundColor: 'var(--color-magenta-tint)',
                                      color: 'var(--color-magenta)',
                                      fontSize: '11px',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {ROLE_LABELS[role] || role}
                                  </span>
                                ))
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                {/* Raw OU memberships */}
                <section>
                  <h2 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 10px 0', color: 'var(--color-black)' }}>
                    All Entra group memberships
                  </h2>
                  <p style={{ margin: '0 0 12px 0', fontSize: '13px' }}>
                    Every Entra group Object ID this user belongs to (synced from Microsoft Graph). Not all of these need to map to access groups.
                  </p>
                  {rolesData.ouMemberships.length === 0 ? (
                    <p
                      style={{
                        padding: '12px 16px',
                        backgroundColor: 'var(--color-light-grey)',
                        borderRadius: '6px',
                        margin: 0,
                        fontSize: '13px',
                      }}
                    >
                      No Entra group memberships on file for this user.
                    </p>
                  ) : (
                    <ul
                      style={{
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        display: 'grid',
                        gap: '4px',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                      }}
                    >
                      {rolesData.ouMemberships.map((oid) => {
                        const matched = rolesData.accessGroups.find((g) => g.entraObjectId === oid)
                        return (
                          <li
                            key={oid}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: matched ? 'var(--color-magenta-tint)' : 'var(--color-light-grey)',
                              borderRadius: '4px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <span>{oid}</span>
                            {matched && (
                              <span style={{ fontFamily: 'var(--font-family)', fontSize: '11px', color: 'var(--color-magenta)' }}>
                                → {matched.displayName}
                              </span>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </section>
              </div>
            )}
          </div>
        )}

        {activeTab === 'audit' && (
          <div style={{ color: 'var(--color-dark-grey)' }}>
            <p>Audit history for this user will appear here.</p>
          </div>
        )}
      </div>

      {/* Edit modal */}
      <Modal
        isOpen={editModalOpen}
        title="Edit user"
        onClose={() => setEditModalOpen(false)}
        isDirty={editData.displayName !== user.displayName || editData.email !== user.email || editData.jobTitle !== (user.jobTitle || '') || editData.department !== (user.department || '')}
      >
          <div style={{ display: 'grid', gap: '16px' }}>
            {[
              { label: 'Display name', key: 'displayName' },
              { label: 'Email', key: 'email' },
              { label: 'Job title', key: 'jobTitle' },
              { label: 'Department', key: 'department' },
            ].map((field) => (
              <div key={field.key}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                  {field.label}
                </label>
                <input
                  type="text"
                  value={editData[field.key as keyof UserData] || ''}
                  onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--color-light-grey)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'var(--font-family)',
                  }}
                />
              </div>
            ))}
          </div>
        </Modal>
    </div>
  )
}

export default UserDetail
